/**
 * DitherLogo - Generates a halftone dither pattern with embedded pixel-font text.
 *
 * Creates a decorative card-back pattern by:
 * 1. Building a grayscale matrix with random colored rectangles + centered text
 * 2. Rendering each pixel as a sized dot (ellipse), where darker pixels = larger dots
 *
 * Uses PDF page embedding for efficient reuse - the pattern is drawn once
 * and embedded as a page that can be drawn multiple times.
 *
 * Ported from C# G<T>.DitherLogo / GetDitherForm / GetDitherMatrix
 */

import { PageGen, PdfRect, PdfBrush, PdfColor } from './pdf';
import { PDFDocument, PDFPage, PDFEmbeddedPage } from 'pdf-lib';
import { PIXEL_FONT } from './pixelFont';
import './draw/primitives';

const ditherFormCache = new Map<string, { doc: PDFDocument; embedded: PDFEmbeddedPage }>();
const ditherMatrixCache = new Map<string, Uint8Array>();

function makeCacheKey(
  text: string,
  widthMm: number,
  heightMm: number,
  seed: number,
  round: boolean,
  colorHex: string,
  cellSize: number
): string {
  return `${text}|${widthMm}|${heightMm}|${seed}|${round}|${colorHex}|${cellSize}`;
}

// Seeded PRNG (mulberry32)
function createRng(seed: number) {
  let s = seed >>> 0;
  return {
    nextInt(max: number): number {
      s += 0x6d2b79f5;
      let t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) % max;
    },
  };
}

/**
 * Generate a grayscale dither matrix - elegant random pattern for card backs.
 *
 * Creates a decorative halftone pattern:
 * - Clean double border
 * - Random clusters of dots (constellation-like)
 * - Scattered accent dots
 * - Subtle diagonal texture
 * - Centered text with clear area
 *
 * @param text  - Text to render centered (A-Z, space)
 * @param width - Matrix width in pixels (in mm)
 * @param height - Matrix height in pixels (in mm)
 * @param seed  - Random seed for deterministic pattern
 * @param round - If true, clip to circular shape (for round cards like Dobble)
 * @returns Column-major Uint8Array of grayscale values [0-255]
 */
function getDitherMatrix(
  text: string,
  width: number,
  height: number,
  seed: number,
  round: boolean,
  cellSize = 12
): Uint8Array {
  const cacheKey = `${text}|${width}|${height}|${seed}|${round}|${cellSize}`;
  const cached = ditherMatrixCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const rng = createRng(seed);
  const matrix = new Uint8Array(width * height);

  // Value levels: 255 = no dot (white), 0 = max dot (black)
  const WHITE = 255;
  const BLACK = 0;
  const DARK = 60;
  const MEDIUM = 120;
  const LIGHT = 180;

  function set(x: number, y: number, v: number): void {
    if (x >= 0 && x < width && y >= 0 && y < height) {
      matrix[x * height + y] = v;
    }
  }

  function fillTo(x: number, y: number, w: number, h: number, v: number): void {
    for (let xx = 0; xx < w; xx++) {
      for (let yy = 0; yy < h; yy++) {
        set(x + xx, y + yy, v);
      }
    }
  }

  // Fill background with white
  fillTo(0, 0, width, height, WHITE);

  // Borders
  const borderOuter = 2;
  const borderInner = 1;
  fillTo(0, 0, width, borderOuter, WHITE);
  fillTo(0, height - borderOuter, width, borderOuter, WHITE);
  fillTo(0, 0, borderOuter, height, WHITE);
  fillTo(width - borderOuter, 0, borderOuter, height, WHITE);

  fillTo(borderOuter, borderOuter, width - 2 * borderOuter, borderInner, WHITE);
  fillTo(
    borderOuter,
    height - borderOuter - borderInner,
    width - 2 * borderOuter,
    borderInner,
    WHITE
  );
  fillTo(borderOuter, borderOuter, borderInner, height - 2 * borderOuter, WHITE);
  fillTo(
    width - borderOuter - borderInner,
    borderOuter,
    borderInner,
    height - 2 * borderOuter,
    WHITE
  );

  const innerX = borderOuter + borderInner;
  const innerY = borderOuter + borderInner;
  const innerW = width - 2 * innerX;
  const innerH = height - 2 * innerY;

  function drawSym(px: number, py: number, v: number) {
    set(px, py, v);
    set(width - 1 - px, height - 1 - py, v);
  }

  function drawL(px: number, py: number, size: number, v: number) {
    for (let i = 0; i < size; i++) {
      drawSym(px + i, py, v);
      drawSym(px, py + i, v);
    }
  }

  // cellSize passed as parameter for flexibility
  const cols = Math.floor(innerW / cellSize);
  const rows = Math.floor(innerH / cellSize);

  for (let c = 0; c <= cols / 2; c++) {
    for (let r = 0; r < rows; r++) {
      const baseX = innerX + c * cellSize;
      const baseY = innerY + r * cellSize;
      if (c === Math.floor(cols / 2) && r > rows / 2) continue;
      if (baseX >= innerX + innerW / 2) continue;

      const x = baseX + rng.nextInt(cellSize - 6) + 3;
      const y = baseY + rng.nextInt(cellSize - 6) + 3;

      const type = rng.nextInt(20);
      const color = [BLACK, DARK, MEDIUM][rng.nextInt(3)];

      if (type < 4) {
        // 3x3 Block
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) drawSym(x + dx, y + dy, color);
        }
      } else if (type < 8) {
        // Hollow Diamond
        drawSym(x, y - 2, color);
        drawSym(x, y + 2, color);
        drawSym(x - 2, y, color);
        drawSym(x + 2, y, color);
        drawSym(x - 1, y - 1, color);
        drawSym(x + 1, y - 1, color);
        drawSym(x - 1, y + 1, color);
        drawSym(x + 1, y + 1, color);
      } else if (type < 12) {
        // Intricate Plus (5x5)
        for (let i = -2; i <= 2; i++) {
          drawSym(x + i, y, color);
          drawSym(x, y + i, color);
        }
      } else if (type < 16) {
        // Large Corner motif
        drawL(x - 2, y - 2, 5, color);
      } else {
        // Compass Star (Large)
        for (let i = -4; i <= 4; i++) {
          if (Math.abs(i) < 2) continue;
          drawSym(x + i, y, MEDIUM);
          drawSym(x, y + i, MEDIUM);
        }
        drawSym(x, y, BLACK);
      }
    }
  }

  // Mask
  if (round) {
    const dw = width / 2;
    const dh = height / 2;
    const r = Math.min(dw, dh) - borderOuter;
    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        if (Math.sqrt((dw - x) ** 2 + (dh - y) ** 2) > r - 0.5) set(x, y, WHITE);
      }
    }
  }

  // Text
  const letters = [...text].map((c) => PIXEL_FONT[c] || PIXEL_FONT[' ']);
  const textW = letters.reduce((sum, l) => sum + l[0].length + 1, 0) - 1;
  const textH = Math.max(...letters.map((l) => l.length));
  const dx = Math.floor((width - textW) / 2);
  const dy = Math.floor((height - textH) / 2);

  const clearMargin = 5;
  for (let x = dx - clearMargin; x < dx + textW + clearMargin; x++) {
    for (let y = dy - clearMargin; y < dy + textH + clearMargin; y++) {
      if (x >= innerX && x < width - innerX && y >= innerY && y < height - innerY) set(x, y, WHITE);
    }
  }

  let px = dx;
  for (const letter of letters) {
    const cw = letter[0].length;
    for (let x = 0; x < cw; x++) {
      for (let y = 0; y < letter.length; y++) {
        if (letter[y][x] !== 0) set(px + x, dy + y, BLACK);
      }
    }
    px += cw + 1;
  }

  ditherMatrixCache.set(cacheKey, matrix);
  return matrix;
}

/**
 * Create a dither form - an embedded PDF page containing the dither pattern.
 * The form is cached and can be reused for multiple card backs.
 */
async function createDitherForm(
  text: string,
  widthMm: number,
  heightMm: number,
  seed: number,
  round: boolean,
  color: PdfColor,
  cellSize = 12
): Promise<{ doc: PDFDocument; embedded: PDFEmbeddedPage }> {
  const colorHex = color.toHexString();
  const cacheKey = makeCacheKey(text, widthMm, heightMm, seed, round, colorHex, cellSize);

  const cached = ditherFormCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const formDoc = await PDFDocument.create();
  const formPage = formDoc.addPage([(widthMm * 72) / 25.4, (heightMm * 72) / 25.4]);

  const matrix = getDitherMatrix(text, widthMm, heightMm, seed, round);
  const pdfColor = color.toPdfLibColor();
  const szW = formPage.getWidth() / widthMm;
  const szH = formPage.getHeight() / heightMm;

  for (let x = 0; x < widthMm; x++) {
    for (let y = 0; y < heightMm; y++) {
      const gray = matrix[x * heightMm + y];
      const v = ((255 - gray) / 512) * 1.1;

      if (v > 0.05) {
        const cx = szW * (x + 0.5);
        const cy = szH * (y + 0.5);
        const rw = szW * v;
        const rh = szH * v;

        formPage.drawEllipse({
          x: cx,
          y: formPage.getHeight() - cy,
          xScale: rw,
          yScale: rh,
          color: pdfColor,
        });
      }
    }
  }

  const embeddedPage = await formDoc.embedPage(formPage);
  const result = { doc: formDoc, embedded: embeddedPage };
  ditherFormCache.set(cacheKey, result);
  return result;
}

/**
 * Draw a dither logo pattern onto a page.
 *
 * Uses PDF page embedding for efficient reuse - the pattern is drawn once
 * and embedded as a page that can be drawn multiple times.
 *
 * @param page  - Target page
 * @param text  - Text to display (A-Z, space)
 * @param color - Dot color
 * @param rect  - Position and size on page (in points)
 * @param seed  - Random seed for pattern
 * @param round - Circular mask (default false)
 */
export async function drawDitherLogo(
  page: PageGen,
  text: string,
  color: PdfColor,
  rect: PdfRect,
  seed: number,
  round = false,
  cellSize = 12,
  dotSize = 1.5
): Promise<void> {
  const widthMm = Math.floor((rect.width * 25.4) / 72 / dotSize);
  const heightMm = Math.floor((rect.height * 25.4) / 72 / dotSize);

  if (widthMm < 10 || heightMm < 10) {
    const matrix = getDitherMatrix(text, widthMm, heightMm, seed, round, cellSize);
    renderDitherMatrixDirect(page, matrix, widthMm, heightMm, color, rect);
    return;
  }

  // For now, use direct rendering to verify pattern works
  // TODO: fix embedPage issue
  const matrix = getDitherMatrix(text, widthMm, heightMm, seed, round, cellSize);
  renderDitherMatrixDirect(page, matrix, widthMm, heightMm, color, rect);
  

  /* Embed approach - not working yet
  const form = await createDitherForm(text, widthMm, heightMm, seed, round, color);

  page.page.drawPage(form.embedded, {
    x: rect.x,
    y: page.page.getHeight() - rect.y - rect.height,
    width: rect.width,
    height: rect.height,
  });
  */
}

/**
 * Render matrix directly (for very small sizes where embedding is not worth it).
 */
function renderDitherMatrixDirect(
  page: PageGen,
  matrix: Uint8Array,
  matrixWidth: number,
  matrixHeight: number,
  color: PdfColor,
  rect: PdfRect
): void {
  const brush = new PdfBrush(color);
  const szW = rect.width / matrixWidth;
  const szH = rect.height / matrixHeight;

  for (let x = 0; x < matrixWidth; x++) {
    for (let y = 0; y < matrixHeight; y++) {
      const gray = matrix[x * matrixHeight + y];
      const v = ((255 - gray) / 512) * 1.1;

      if (v > 0.05) {
        const cx = rect.x + szW * (x + 0.5);
        const cy = rect.y + szH * (y + 0.5);
        const rw = szW * v;
        const rh = szH * v;
        page.drawEllipse(null, brush, new PdfRect(cx - rw, cy - rh, rw * 2, rh * 2));
      }
    }
  }
}

export function clearDitherCache(): void {
  ditherMatrixCache.clear();
  ditherFormCache.clear();
}
